<template>
  <div class="gw-nok-items-chart">
    <div :style="canvasWrapperStyle">
      <canvas ref="chart" class="f-h f-w" />
    </div>
  </div>
</template>

<script>
/**
 * GW Checklist items pareto chart. First items will be the items with
 * the most NOK in the selected period of time
 *
 * @vue-prop {Array} gwData - Completed GW data
 * @vue-prop {Number} groupBy - 0: group bars by checklist item templates,
 *                              1: by zones,
 *                              2: by audited team
 */

import { Chart } from 'chart.js';
// Vuex
import { mapState } from 'vuex';
import { FETCH_GW_CL_ITEM_TEMPLATES, FETCH_ZONES, FETCH_TEAMS } from '@/store/action-types';
import { GET_GW_CL_ITEM_TEMPLATES, GET_ZONES, GET_TEAMS } from '@/store/mutation-types';
// Utils
import { nestedFlatten } from '@/utils/main';
import { SColors } from '@/utils/constants';

export default {
  name: 'GwNOKItemsChart',

  props: {
    gwData: {
      type: Array,
      required: true,
    },
    groupBy: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      chart: null,
    };
  },

  computed: {
    /** Returns a callback to increment NOK values according to which chart should be displayed */
    incrementNokCallback() {
      if (this.groupBy === 0) {
        return (idNOKs, item) => {
          if (item.ok === 0 && item.template !== null) {
            idNOKs[`${item.template}`] = (idNOKs[`${item.template}`] || 0) + 1;
          }
        };
      }
      if (this.groupBy === 1) {
        return (idNOKs, item, step) => {
          if (item.ok === 0 && step.zone > 0) {
            idNOKs[`${step.zone}`] = (idNOKs[`${step.zone}`] || 0) + 1;
          }
        };
      }
      return (idNOKs, item, step) => {
        if (item.ok === 0 && step.auditedTeam > 0) {
          idNOKs[`${step.auditedTeam}`] = (idNOKs[`${step.auditedTeam}`] || 0) + 1;
        }
      };
    },
    /** Object formatted as { <id>: <number_of_nok>, ... } */
    nokPerIds() {
      const idNOKs = {};
      this.gwData.forEach((gw) => {
        (gw.steps || []).forEach((step) => {
          (step.checklists || []).forEach((cl) => {
            (cl.items || []).forEach((item) => {
              this.incrementNokCallback(idNOKs, item, step);
            });
          });
        });
      });
      return idNOKs;
    },
    /** Object formatted as { <id>: <label_to_display> } */
    labelById() {
      const idLabels = {};
      let items = this.itemTemplates;
      let labelKey = 'text';
      if (this.groupBy === 1) {
        items = nestedFlatten(this.zones, 'zones');
        labelKey = 'name';
      } else if (this.groupBy === 2) {
        items = this.teams;
        labelKey = 'name';
      }
      items.forEach((item) => { idLabels[`${item.id}`] = item[labelKey]; });
      return idLabels;
    },
    chartData() {
      const maxTextLength = 10;
      // Pareto bars
      const data = [];
      Object.keys(this.nokPerIds).forEach((templateId) => {
        const templateName = this.labelById[templateId] || '';
        data.push({
          value: this.nokPerIds[templateId],
          name: templateName,
          label: templateName.length > maxTextLength
            ? `${templateName.substring(0, maxTextLength)}...`
            : templateName,
        });
      });
      data.sort((e1, e2) => e2.value - e1.value);
      const usedData = data.slice(0, 10);

      return {
        datasets: [
          {
            data: usedData.map(it => it.value),
            borderWidth: 0,
            backgroundColor: SColors.statsBlue,
            maxBarThickness: 8,
            borderRadius: 200,
          },
        ],
        itemNames: usedData.map(it => it.name),
        labels: usedData.map(it => it.label),
      };
    },
    canvasWrapperStyle() {
      return {
        height: '100%',
      };
    },
    ...mapState({
      itemTemplates: state => state[GET_GW_CL_ITEM_TEMPLATES.stateKey] || [],
      itemTemplatesLoading: state => state[GET_GW_CL_ITEM_TEMPLATES.loadingKey],
      zones: state => state[GET_ZONES.stateKey] || [],
      zonesLoading: state => state[GET_ZONES.loadingKey],
      teams: state => state[GET_TEAMS.stateKey] || [],
      teamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
  },

  watch: {
    gwData() {
      if (!this.itemTemplatesLoading) {
        this.createChart();
      }
    },
    itemTemplatesLoading(loading) {
      if (!loading) {
        this.createChart();
      }
    },
  },

  mounted() {
    if (this.groupBy === 0 && !this.itemTemplatesLoading && this.currentTeam) {
      this.$store.dispatch(FETCH_GW_CL_ITEM_TEMPLATES);
    }
    if (this.groupBy === 1 && !this.zonesLoading && this.currentTeam) {
      this.$store.dispatch(FETCH_ZONES);
    }
    if (this.groupBy === 2 && !this.teamsLoading && this.currentTeam) {
      this.$store.dispatch(FETCH_TEAMS);
    }
    this.createChart();
  },

  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    createChart() {
      const canvas = this.$refs.chart;
      if (!canvas) {
        return;
      }
      if (this.chart) {
        this.chart.destroy();
      }
      const context = canvas.getContext('2d');
      this.chart = new Chart(context, {
        type: 'bar',
        data: this.chartData,
        options: {
          spanGaps: true,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              max: this.chartData.max,
              grid: {
                color: SColors.grayBgHover,
                borderDash: [3, 3],
                tickColor: 'rgba(0, 0, 0, 0)',
                borderColor: 'rgba(0, 0, 0, 0)',
              },
            },
            x: {
              grid: {
                drawTicks: false,
                color: 'transparent',
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                title: item => this.chartData.itemNames[item[0].dataIndex],
                label: (item) => {
                  const { dataIndex, datasetIndex } = item;
                  const { data } = item.dataset;
                  let val = data[dataIndex];
                  let prevVal = data[dataIndex - 1];
                  let diff;
                  if (prevVal) {
                    diff = val - prevVal;
                    diff = Number.isInteger(diff) ? diff : diff.toFixed(2);
                    prevVal = Number.isInteger(prevVal) ? prevVal : prevVal.toFixed(2);
                  }
                  val = Number.isInteger(val) ? val : val.toFixed(2);

                  return datasetIndex === 0
                    ? this.$tc('message.GW_N_NOK_ITEMS', val, { nb: val })
                    : `${val}${prevVal ? `% (${prevVal} + ${diff})` : '%'}`;
                },
              },
            },
          },
        },
      });
    },
  },
};
</script>
